// ─── Telling the server that the client broke ────────────────────────────────
//
// Farndale's `client/src/lib/reportClientError.ts`, moved here on 2026-09-25
// so it stops being one app's good idea. What changed is the base path, which
// is a constructor argument now because Crafting Comb and Memorial Hall both
// mount their router at `/api` but nothing says a third product must.
//
// ── The incident this is here for ───────────────────────────────────────────
//
// Memorial Hall went black-screen on Members and Proposals during a live demo
// to a business contact, and there was NOTHING to diagnose it from: no
// boundary, no report, no log line. The likeliest cause is a stale bundle
// against a restarted server during the rotation-loop chaos of that night, and
// "likeliest" is as far as anybody can get, because the evidence was never
// written down anywhere. That is the actual failure — not the crash, the
// silence after it.
//
// ── Why every path here swallows its own failure ────────────────────────────
//
// This runs when the app is already broken. A reporter that throws while
// reporting turns one crash into two and loses the first one, so the fetch
// cannot reject the caller and the whole body is wrapped besides. `keepalive`
// because a crash is very often followed by the person closing the tab, and a
// normal fetch is cancelled on unload — which would lose exactly the reports
// worth having.

let endpoint = "/api/client-error";

/** Point the reporter at a server mounted somewhere other than `/api`. */
export function setClientErrorEndpoint(path: string): void {
  endpoint = path;
}

export function reportClientError(
  source: string,
  error: unknown,
  extra?: Record<string, unknown>,
): void {
  try {
    const message = error instanceof Error ? error.message : String(error);
    const stack = error instanceof Error ? error.stack : undefined;
    void fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ source, message, stack, ...extra }),
      keepalive: true,
    }).catch(() => { /* best-effort — nothing to do if this fails too */ });
  } catch {
    /* never let error reporting itself throw */
  }
}

/**
 * The half an error boundary cannot catch.
 *
 * A React boundary only sees errors thrown during render. An exception in an
 * event handler, a failed image decode, an unhandled rejection in an async
 * click — none of those reach it, and all of them can leave a screen wedged.
 * Farndale wires both of these in its own `main.tsx`; carrying only the
 * boundary across would have brought half the pattern and looked complete.
 *
 * Idempotent, because a double-registered listener means every crash is
 * reported twice and the log stops being countable.
 */
let installed = false;

export function installCrashReporting(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", event => {
    reportClientError("window.onerror", event.error ?? event.message);
  });
  window.addEventListener("unhandledrejection", event => {
    reportClientError("unhandledrejection", event.reason);
  });
}
